import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import type { Prayer } from '@/types/prayer';
import { whoOf, formatFullDate } from './prayer-utils';
import i18n from '@/i18n';
import PrayerShareCard from '@/components/PrayerShareCard';
import type { CardSize } from '@/lib/share-card-truncation';

function sanitizeFilename(s: string): string {
  return s.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Human-friendly localized "Wed, Apr 24 at 7:03 am" for the card +
 * SMS fallback text. Not using prayer-utils' formatFullDateTime
 * because that's hardcoded en-US and serves the app's own stacking
 * UI; the share artifact should localize to the user's current
 * language so recipients of the SAME language see natural formatting.
 */
export function formatShareTimestamp(iso: string, locale: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(d);
  // No hour12 — let the locale pick its native convention. English
  // gets "4:53 PM"; Spanish/Portuguese get "16:53".
  const time = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  return `${date} · ${time}`;
}

export function formatPrayerExport(prayer: Prayer): string {
  const who = whoOf(prayer);
  const displayWho = who === 'Self' ? 'You' : who;
  const created = formatFullDate(prayer.createdAt);
  const lines: string[] = [];

  lines.push(`Prayer for ${displayWho}`);
  lines.push(created);
  lines.push('');
  lines.push(prayer.rawText);

  const history = prayer.progressHistory ?? [];
  if (history.length > 0) {
    lines.push('');
    lines.push('---');
    lines.push('');
    for (const entry of [...history].reverse()) {
      const date = formatFullDate(entry.date);
      lines.push(`${date}`);
      lines.push(entry.note);
      lines.push('');
    }
  }

  lines.push('---');
  lines.push('Saved from watch Him work');

  return lines.join('\n');
}

function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function buildPrayerPdf(prayer: Prayer): Promise<Blob> {
  const [{ pdf }, { PrayerDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('@/components/PrayerDocument'),
  ]);
  // Compute the props PrayerDocument used to derive internally before
  // the Phase B Task 0 i18n-decoupling refactor. Each expression is
  // identical to the one the component used itself, so the rendered
  // PDF is byte-equivalent to the pre-refactor browser output.
  const who = whoOf(prayer);
  const displayWho = who === 'Self' ? 'You' : who;
  const language = i18n.language || 'en';
  const psalm = i18n.t('evidence.psalm', {
    defaultValue: 'I will remember the deeds of the LORD',
  }) as string;
  const footerLabel = i18n.t('pray.pdfFooter', {
    defaultValue: 'Personal reference',
  }) as string;
  return pdf(
    <PrayerDocument
      prayer={prayer}
      displayWho={displayWho}
      language={language}
      psalm={psalm}
      footerLabel={footerLabel}
    />,
  ).toBlob();
}

// The legacy requestPersonBookExport initial-POST helper was REMOVED in
// the S0 cutover (PC-B rev 9): the export path is now the preview-bound
// v1 protocol in src/lib/keepsake/client.ts (beginKeepsakePreview ->
// requestKeepsakeExport -> poll/delivery), and the legacy body shape
// {who, displayName, language} no longer exists server-side.

/**
 * Hand the rendered keepsake Blob to Web Share if available,
 * falling back to a direct anchor download otherwise.
 *
 * iOS user-activation gap (load-bearing UX caveat): on async
 * resolutions, the 30-90 s polling window between the user's tap
 * and `deliverKeepsake` being called means `navigator.share()`
 * rejects on iOS Safari with `NotAllowedError` because the
 * original tap is no longer the "live" user gesture. Mandatory
 * add #4 of the chunk 1.6.6 plan: console.warn the rejection so
 * Gate G observability captures the fall-through; downstream
 * anchor-download path runs without user activation since it's
 * a same-origin synthesized click. Sync resolutions don't
 * exhibit this — the original tap is still live when the 200
 * comes back.
 */
export function deliverKeepsake(blob: Blob, displayName: string): void {
  const title = `Prayers for ${displayName}`;
  const filename = `${sanitizeFilename(title)}.pdf`;
  const file = new File([blob], filename, { type: 'application/pdf' });

  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (nav.canShare && nav.canShare({ files: [file] }) && nav.share) {
    nav
      .share({ title, files: [file] })
      .catch((err) => {
        // iOS user-activation gap on async resolutions; ops/Gate G
        // observability per mandatory add #4. NOT user-facing.
        console.warn(
          '[keepsake] share failed, falling through to direct download',
          err,
        );
        downloadFile(file);
      });
    return;
  }

  downloadFile(file);
}

export interface PrayerShareContent {
  /**
   * Proof-line prefix, rendered as the first of two stacked gold lines
   * at the top of the card. Carries the trailing colon, e.g.
   * "I prayed for you on:". Composed by the caller from
   * `t('share.cardMetaPrefixColon')` / `cardMetaPrefixColonGeneral`.
   */
  prayedOnPrefix: string;
  /**
   * Proof-line datetime, rendered as the second of the two stacked
   * gold lines, e.g. "Wed, Apr 24 · 4:53 PM". Composed by the caller
   * via `formatShareTimestamp`. Splitting prefix + datetime into two
   * lines was Ship 2a.2's response to readability feedback — the old
   * single-line shape combined the two into one wrapped paragraph
   * that read as a single dense statement.
   */
  prayedOnDatetime: string;
  /**
   * Optional curated verse text. Ship 2a.3 renders this in full — no
   * caller truncation, no CSS clamp. Caller is responsible for picking
   * a `cardSize` that keeps the combined verse + prose render within
   * the canvas-safe bound (see src/lib/share-card-truncation.ts
   * `pickCardSize`).
   */
  verseText?: string;
  /** Optional verse reference, e.g. "Psalm 147:3". */
  verseReference?: string;
  /**
   * Optional already-prefixed-and-stripped card label line, e.g.
   * "Prayed for healing". B2a.3 replaces the prior summaryLine —
   * coarser, message-register, recipient-safe. Caller computes from
   * `t('share.cardLabelPrefix') + ' ' + stripCardLabelDisambig(prayer.parsed.cardLabel)`
   * (see src/lib/card-label.ts). Never raw prayer text; never gardenSummary.
   */
  cardLabelLine?: string;
  /**
   * Verse size class. Caller derives via
   * `pickCardSize(effectiveVerseLen)` in share-card-truncation.ts and
   * passes it through so PrayerShareCard picks .verseLg/Md/Sm based
   * on verse length — long verses pick the smaller class to stay
   * inside the canvas-safe bound. Undefined when no verse — slot
   * doesn't render in that state.
   */
  cardSize?: CardSize;
}

export interface PrayerShareAsset {
  /** Raw PNG blob for clipboard-write (`navigator.clipboard.write`). */
  blob: Blob;
  /** Base64 data URL for inline `<img src>` preview rendering. */
  dataUrl: string;
  /** Wrapped as `File` so the caller can hand it to download/Web Share. */
  file: File;
  /**
   * Layout (CSS) pixel dimensions of the captured card. PNG on disk
   * is 2× these (pixelRatio 2). Ship 2a.3: width is always 1080;
   * height is measured after fonts + layout settle and grows past
   * 2400 when verse + prose content stack tall. Consumers (the
   * in-sheet preview slot, the FullCardPreview modal) read these to
   * preserve the actual PNG aspect — without them, the preview
   * frame would letterbox tall cards or fall back to a fixed 9:20
   * shape that no longer matches the captured artifact.
   */
  width: number;
  height: number;
}

/**
 * Build the prayer share card PNG once, return all three handles
 * (blob / dataUrl / file) so the caller can preview it inline AND
 * copy-to-clipboard AND download without rebuilding.
 *
 * The card carries only B0 safe content (proof line + optional capped
 * summary) — NEVER the raw prayer body. `prayer` is used only to derive
 * the download filename via `whoOf`; no prayer text crosses into the image.
 *
 * Caller MUST check `prayer.isPrivate` and gate behind a confirmation
 * dialog before calling — this helper does not re-check.
 */
export async function buildPrayerShareImage(
  prayer: Prayer,
  content: PrayerShareContent,
  resolvedWho = whoOf(prayer),
): Promise<PrayerShareAsset> {
  return await buildShareImageAsset(content, resolvedWho);
}

/**
 * Save the already-built card image to the user's downloads — the
 * Download path on the share sheet. Split from `buildPrayerShareImage`
 * so the caller (the sheet component) can render the preview first
 * and let the user tap Copy or Download on the SAME artifact.
 */
export function downloadPrayerShareImage(asset: PrayerShareAsset): void {
  downloadFile(asset.file);
}

async function buildShareImageAsset(
  content: PrayerShareContent,
  who: string,
): Promise<PrayerShareAsset> {
  const { toPng } = await import('html-to-image');

  // Host element lives off-screen (positioned by the card's own
  // `.stage` class). We create a fresh root per share so React
  // state across multiple rapid shares never leaks.
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);

  let target: HTMLDivElement | null = null;
  try {
    flushSync(() => {
      root.render(
        <PrayerShareCard
          ref={(el) => {
            target = el;
          }}
          prayedOnPrefix={content.prayedOnPrefix}
          prayedOnDatetime={content.prayedOnDatetime}
          verseText={content.verseText}
          verseReference={content.verseReference}
          cardLabelLine={content.cardLabelLine}
          cardSize={content.cardSize}
        />,
      );
    });

    if (!target) throw new Error('share card did not mount');
    // The ref points to `.stage` (the outer wrapper); the inner
    // `.card` is the 1080×N layout artifact we want to capture
    // (captured at pixelRatio 2 → 2160×2N PNG on disk). Ship 2a.3:
    // height is variable — measured after fonts + layout settle.
    // Reaching through one child is safer than exposing a second ref.
    const cardEl = (target as HTMLElement).querySelector<HTMLElement>(':scope > div');
    if (!cardEl) throw new Error('share card content missing');

    // Let webfonts + SVG layout settle before capture. document.fonts.ready
    // blocks until all font downloads complete, and two rAFs flush layout.
    // Skipping either produces blank PNGs on the first share of an
    // iOS Safari session (known html-to-image foreignObject quirk).
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      try {
        await document.fonts.ready;
      } catch {
        // Continue even if font readiness can't be awaited.
      }
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Ship 2a.3 — measure after fonts + layout settle (the guarded
    // fonts.ready chain above is load-bearing for this measurement).
    // Math.ceil avoids sub-pixel content clipping. Math.max(2400, …)
    // guards against CSS min-height vs JS measurement rounding drift —
    // the card has min-height: 2400px so the floor matches the visual
    // contract for short cards (proof-only, proof+verse only).
    const CARD_WIDTH = 1080;
    const CARD_MIN_HEIGHT = 2400;
    const measuredHeight = Math.max(
      CARD_MIN_HEIGHT,
      Math.ceil(cardEl.getBoundingClientRect().height),
    );

    const dataUrl = await toPng(cardEl, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#1a1816',
      width: CARD_WIDTH,
      height: measuredHeight,
      // The card's only web font is Inter, served cross-origin from Google
      // Fonts. html-to-image's font-embed step tries to read that stylesheet's
      // cssRules to inline them and the browser blocks it with a SecurityError
      // (cross-origin), so NO font is ever embedded — the capture rasterizes
      // with whatever the page already loaded (Inter when ready, else the
      // -apple-system fallback). Skipping the embed produces the SAME PNG while
      // dropping the console errors and the wasted failed cross-origin fetches.
      skipFonts: true,
    });

    const blob = await (await fetch(dataUrl)).blob();
    // General drops the "for X" suffix since "Prayer for General" reads
    // awkwardly on disk; the captured PNG itself never names a recipient.
    const filename = who === 'General'
      ? `${sanitizeFilename('Prayer')}.png`
      : `${sanitizeFilename(`Prayer for ${who === 'Self' ? 'You' : who}`)}.png`;
    const file = new File([blob], filename, { type: 'image/png' });
    return { blob, dataUrl, file, width: CARD_WIDTH, height: measuredHeight };
  } finally {
    // Unmount + detach in the same tick whether or not capture
    // succeeded, so we never leak hidden DOM across shares.
    root.unmount();
    host.remove();
  }
}

export async function sharePrayerExport(prayer: Prayer): Promise<void> {
  const who = whoOf(prayer);
  const displayWho = who === 'Self' ? 'You' : who;
  const title = `Prayer for ${displayWho}`;
  const filename = `${sanitizeFilename(title)}.pdf`;

  let file: File;
  try {
    const blob = await buildPrayerPdf(prayer);
    file = new File([blob], filename, { type: 'application/pdf' });
  } catch {
    // PDF generation failed — fall back to plain text via clipboard
    try {
      await navigator.clipboard.writeText(formatPrayerExport(prayer));
    } catch {
      // Nothing else to try
    }
    return;
  }

  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (nav.canShare && nav.canShare({ files: [file] }) && nav.share) {
    try {
      await nav.share({ title, files: [file] });
      return;
    } catch {
      // User cancelled or share failed — fall through to direct download
    }
  }

  try {
    downloadFile(file);
  } catch {
    try {
      await navigator.clipboard.writeText(formatPrayerExport(prayer));
    } catch {
      // Nothing else to try
    }
  }
}
